import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCheck, FiInfo } from 'react-icons/fi';
import AppButton from '../../../../shared/components/app-button/AppButton';
import CountdownTimer from '../../../../shared/components/countdown-timer/CountdownTimer';
import { AppConfirmationContext } from '../../../../shared/components/app-confirmation/AppConfirmationProvider';
import { getAgentBalanceApi } from '../../../../api/profileApi';
import {
  getPGCheckOutAmountApi,
  getPaymentGateway,
} from '../../../../api/flightApi';
import { getUserAgentId } from '../../../../shared/helper-utils/localStorage';
import CruiseLayout, {
  CruiseBackLink,
} from '../../components/common/CruiseLayout';
import CruiseGuestForm from '../../components/CruiseGuestForm/CruiseGuestForm';
import CruiseFareSummary from '../../components/CruiseFareSummary/CruiseFareSummary';
import CruisePayment from '../../components/CruisePayment/CruisePayment';
import { bookCruiseApi } from '../../api/cruiseApi';
import {
  BOOKING_TIME_LIMIT_MS,
  CRUISE_ROUTES,
  WALLET_MODE,
} from '../../constants/cruiseConstants';
import { flowUpTo, useCruiseFlow } from '../../utils/cruiseFlow';
import { hasErrors, validateGuest } from '../../utils/cruiseGuestValidation';
import {
  bedLabel,
  blankGuest,
  cabinShortLabel,
  cruiseTitle,
  diningLabel,
  formatAmount,
  formatDateRange,
  gatewayModes,
  guestCount,
  guestsSummary,
  paxTypesOf,
  penaltyRows,
  readInvoice,
  titleCase,
  toNumber,
  wholePartyTotal,
} from '../../utils/cruiseUtils';

const ADDRESS_FIELDS = ['address', 'city', 'state', 'country', 'pinCode'];

const initialGuests = (search, saved) =>
  paxTypesOf(search || {}).map((type, i) =>
    saved?.[i]?.paxType === type
      ? { ...blankGuest(type), ...saved[i] }
      : blankGuest(type),
  );

function CruiseGuestsPage() {
  const navigate = useNavigate();
  const { showAppConfirmationDialog } = useContext(AppConfirmationContext);
  const flow = useCruiseFlow();
  const { search, cruise, fare, category, cabin, bed, dining, priceCheck } =
    flow;
  const ready = Boolean(cruise && fare && category && cabin && priceCheck);

  const invoice = useMemo(() => readInvoice(priceCheck?.invoice), [priceCheck]);

  const [guests, setGuests] = useState(() =>
    initialGuests(search, flow.guests?.list),
  );
  const [sameAddress, setSameAddress] = useState(
    flow.guests?.sameAddress !== undefined ? flow.guests.sameAddress : true,
  );
  const [payMode, setPayMode] = useState(WALLET_MODE);
  const [pg, setPg] = useState('');
  const [pgCharges, setPgCharges] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [payModes, setPayModes] = useState([WALLET_MODE]);
  const [agree, setAgree] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  // Fields the agent has left (blur / pick); their errors show straight away.
  const [touched, setTouched] = useState({});
  // 10 minutes from the price check (kept in the flow, so a refresh does
  // not restart it).
  const [deadline] = useState(
    () => (flow.priceCheckedAt || Date.now()) + BOOKING_TIME_LIMIT_MS,
  );
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const bookingRef = useRef(false);

  // A successful book replaces this history entry with the confirmation,
  // so Back can never reach this page with a used price check.
  useEffect(() => {
    if (!ready) navigate(CRUISE_ROUTES.LANDING, { replace: true });
  }, [ready, navigate]);

  // Online modes are whatever gateways are set up for this agent; the
  // wallet is always offered.
  useEffect(() => {
    let active = true;
    getPaymentGateway(getUserAgentId()).then((res) => {
      if (!active) return;
      const list =
        res?.Root?.Result?.AvailablePaymentGateways?.AvailablePaymentGateway;
      setPayModes([WALLET_MODE, ...gatewayModes(list)]);
    });
    return () => {
      active = false;
    };
  }, []);

  // Wallet balance, same figure the header shows.
  useEffect(() => {
    let active = true;
    getAgentBalanceApi()
      .then((res) => {
        const acc = res?.ApiStatus?.Result?.UserAccount;
        if (active && res?.ApiStatus?.Status === 'OK' && acc) {
          setWalletBalance(
            toNumber(acc.Balance) +
              toNumber(acc.CreditLimit) -
              toNumber(acc.Due),
          );
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  // The agency amount from the payment schedule is what the agent pays;
  // without one, the gross total.
  let payable = 0;
  if (invoice) {
    payable = invoice.netPayable !== null ? invoice.netPayable : invoice.total;
  }

  // Gateway charges for the chosen gateway (shown only if the backend
  // answers them).
  useEffect(() => {
    setPgCharges(null);
    if (!invoice || payMode === WALLET_MODE || !pg) return undefined;
    let active = true;
    getPGCheckOutAmountApi(
      payMode,
      pg,
      '',
      payable,
      'cruise',
      getUserAgentId(),
      '',
    ).then((res) => {
      const r = res?.ApiStatus?.Result;
      if (active && res?.ApiStatus?.Status === 'OK' && r) {
        setPgCharges({
          charges: toNumber(r.PgCharges),
          gst: toNumber(r.GSTOnPgCharges),
          total: toNumber(r.TotalInvoice),
        });
      }
    });
    return () => {
      active = false;
    };
  }, [payMode, pg]);

  if (!ready) return null;

  const needsAddress = (i) => i === 0 || !sameAddress;
  const errors = guests.map((g, i) =>
    validateGuest(g, {
      lead: i === 0,
      needsAddress: needsAddress(i),
      sailDate: cruise.startDate,
      endDate: cruise.endDate,
    }),
  );

  const visibleErrors = (i) => {
    if (showErrors) return errors[i];
    return Object.fromEntries(
      Object.entries(errors[i]).filter(([f]) => touched[`${i}.${f}`]),
    );
  };
  const touch = (i, field) =>
    setTouched((t) =>
      t[`${i}.${field}`] ? t : { ...t, [`${i}.${field}`]: true },
    );

  const updateGuest = (i, field, value) =>
    setGuests((list) => {
      const next = [...list];
      next[i] = { ...next[i], [field]: value };
      return next;
    });

  const chargeAmount =
    payMode !== WALLET_MODE && pgCharges?.total ? pgCharges.total : payable;

  const quoted = wholePartyTotal(category);
  const priceChanged =
    invoice && quoted > 0 && Math.abs(invoice.total - quoted) >= 1;
  const penalties = penaltyRows(invoice?.penalties);

  const toPaxList = () => {
    const lead = guests[0];
    return guests.map((g, i) => {
      const addr = needsAddress(i)
        ? g
        : ADDRESS_FIELDS.reduce((o, f) => ({ ...o, [f]: lead[f] }), {});
      return {
        salutation: g.salutation,
        firstName: g.firstName.trim(),
        middleName: g.middleName.trim(),
        lastName: g.lastName.trim(),
        gender: g.gender,
        dateOfBirth: g.dateOfBirth,
        email: g.email.trim(),
        phoneNo: g.phoneNo,
        countryCode: g.countryCode,
        areaCode: '',
        address: addr.address,
        city: addr.city,
        state: addr.state,
        country: addr.country,
        pinCode: addr.pinCode,
        zipCode: addr.pinCode,
        paxType: g.paxType,
        leadPax: i === 0,
        passportNumber: g.passportNumber,
        passportExpiry: g.passportExpiry,
        passportIssuePlace: g.passportIssuePlace,
        panNumber: g.panNumber,
        nationality: g.nationality,
        diningCode: dining?.diningCode || '',
        bedPreference: bed?.bedCode || '',
      };
    });
  };

  const book = async () => {
    if (bookingRef.current) return;
    bookingRef.current = true;
    setLoading(true);
    const req = {
      responseId: priceCheck.responseId,
      bookingKey: priceCheck.bookingKey,
      fareSource: cruise.fareSource,
      bedCode: bed?.bedCode || '',
      bedDescription: bed?.bedName || '',
      tableSize: guestCount(search),
      diningCode: dining?.diningCode || '',
      diningName: dining?.diningName || '',
      bookingRemark: '',
      paxList: toPaxList(),
      nextraBreakupenc: '',
    };
    const res = await bookCruiseApi(req, {
      paymentmode: payMode,
      pgname: payMode === WALLET_MODE ? '' : pg,
    });
    setLoading(false);

    if (res.ok && res.pgUrl) {
      bookingRef.current = false;
      window.open(res.pgUrl, '_blank');
      setAlert({
        variant: 'success',
        title: 'Complete payment',
        description:
          'The payment page has opened in a new tab. Your booking is confirmed once the payment succeeds.',
      });
      return;
    }

    if (res.ok && res.bookingRef) {
      navigate(CRUISE_ROUTES.CONFIRMATION, {
        replace: true,
        state: flowUpTo(flow, 'priceCheck', {
          guests: { list: guests, sameAddress },
          booking: { ref: res.bookingRef, amount: chargeAmount, payMode },
        }),
      });
      return;
    }

    bookingRef.current = false;
    setAlert({
      variant: 'error',
      title: 'Booking failed',
      description: res.network
        ? 'Could not reach the server. Check My Bookings before trying again, the booking may have gone through.'
        : res.message || 'The cruise line could not confirm this booking.',
    });
  };

  const handlePay = () => {
    if (Date.now() >= deadline) {
      setAlert({
        variant: 'error',
        title: 'Time expired',
        description:
          'The 10 minutes to complete this booking are over. Go back to the cabin to re-check the price.',
      });
      return;
    }
    setShowErrors(true);
    if (hasErrors(errors)) {
      setAlert({
        variant: 'error',
        title: 'Guest details',
        description: 'Please complete the highlighted guest details.',
      });
      const first = document.querySelector('.cruise-module .app-error-text');
      if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (!agree) {
      setAlert({
        variant: 'error',
        title: 'Terms',
        description: 'Please accept the terms & conditions.',
      });
      return;
    }
    if (payMode !== WALLET_MODE && !pg) {
      setAlert({
        variant: 'error',
        title: 'Payment',
        description: 'No payment gateway is available for this mode.',
      });
      return;
    }
    if (
      payMode === WALLET_MODE &&
      walletBalance !== null &&
      walletBalance < payable
    ) {
      setAlert({
        variant: 'error',
        title: 'Insufficient balance',
        description: `Your available balance is ${formatAmount(
          walletBalance,
        )}. Choose another payment mode or top up your wallet.`,
      });
      return;
    }
    showAppConfirmationDialog(
      'Confirm booking',
      payMode === WALLET_MODE
        ? `${formatAmount(payable)} will be debited from your wallet. Proceed?`
        : `You will be taken to the payment page to pay ${formatAmount(
            chargeAmount,
          )}. Proceed?`,
      book,
    );
  };

  return (
    <CruiseLayout
      step="guests"
      loading={loading}
      alert={alert}
      onAlertHide={() => setAlert(null)}
    >
      <CruiseBackLink
        label="Back to cabin"
        onClick={() =>
          navigate(CRUISE_ROUTES.CABIN, {
            state: flowUpTo(flow, 'dining', {
              guests: { list: guests, sameAddress },
            }),
          })
        }
      />
      <div className="cr-split cr-split--with-bar">
        <section className="cr-split__main">
          <div className="cr-timer-bar">
            <span>Complete this booking within</span>
            <CountdownTimer
              targetDate={deadline}
              continueBtnText="Re-check price"
              continueRoutePath="searchResults"
            />
            <span className="cr-muted-sm">
              The price is held for 10 minutes after it is confirmed.
            </span>
          </div>
          <div className="cr-notice cr-notice--success">
            <FiCheck size={18} strokeWidth={2.2} />
            <span>
              <b>Price confirmed</b> with {cruise.cruiseLineName} for{' '}
              {cabinShortLabel(cabin)}, category {category.categoryCode}.
            </span>
          </div>
          {priceChanged && (
            <div className="cr-notice cr-notice--warning">
              <FiInfo size={18} />
              <span>
                The cruise line updated the price from{' '}
                <b>{formatAmount(quoted)}</b> to{' '}
                <b>{formatAmount(invoice.total)}</b>.
              </span>
            </div>
          )}

          {penalties.length > 0 && (
            <div className="cr-panel cr-panel--pad">
              <b className="cr-h4">Cancellation policy</b>
              <div className="cr-table">
                {penalties.map((p) => (
                  <div className="cr-table__row" key={p.key}>
                    <span>{p.when}</span>
                    <b>{p.what}</b>
                  </div>
                ))}
              </div>
              <div className="cr-muted-sm">
                Penalties are set by the cruise line and apply per booking.
              </div>
            </div>
          )}

          <div className="cr-section-head">
            <b className="cr-h3">Guest details ({guests.length})</b>
            <span className="cr-muted-sm">
              Names must match passports exactly
            </span>
          </div>

          {guests.map((g, i) => (
            <CruiseGuestForm
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              index={i}
              guest={g}
              lead={i === 0}
              showAddress={needsAddress(i)}
              sameAddress={sameAddress}
              onToggleSameAddress={() => setSameAddress((s) => !s)}
              errors={visibleErrors(i)}
              onChange={(field, value) => updateGuest(i, field, value)}
              onTouch={(field) => touch(i, field)}
              sailDate={cruise.startDate}
              endDate={cruise.endDate}
            />
          ))}

          <CruisePayment
            modes={payModes}
            mode={payMode}
            onModeChange={(m) => {
              setPayMode(m);
              setPg('');
            }}
            pg={pg}
            onPgChange={setPg}
            walletBalance={walletBalance}
            agree={agree}
            onToggleAgree={() => setAgree((a) => !a)}
            showErrors={showErrors}
          />
        </section>

        <aside className="cr-split__aside cr-split__aside--wide">
          <div className="cr-panel cr-panel--pad cr-mini-trip">
            <b className="cr-trip__title">{cruiseTitle(cruise.cruiseName)}</b>
            <div className="cr-muted-sm">
              {cruise.shipName} ·{' '}
              {formatDateRange(cruise.startDate, cruise.endDate)}
            </div>
            <div className="cr-body-sm">
              {[
                `${category.categoryCode} ${titleCase(category.categoryName)}`,
                cabinShortLabel(cabin),
                bed ? bedLabel(bed) : '',
                dining ? diningLabel(dining) : '',
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
          <CruiseFareSummary
            invoice={invoice}
            guestsLabel={guestsSummary(search)}
            pgCharges={payMode === WALLET_MODE ? null : pgCharges}
          >
            <AppButton
              btnText={`Book & Pay ${formatAmount(chargeAmount)}`}
              className="cr-hide-mobile"
              onClick={handlePay}
              disabled={loading}
              style={{ height: '48px', fontWeight: 700, fontSize: '15px' }}
            />
            {payMode === WALLET_MODE && walletBalance !== null && (
              <div className="cr-muted-xs cr-center">
                Wallet balance after booking:{' '}
                {formatAmount(walletBalance - payable)}
              </div>
            )}
          </CruiseFareSummary>
        </aside>
      </div>

      <div className="cr-mobile-bar">
        <div>
          <div className="cr-muted-xs">
            {invoice?.netPayable !== null ? 'Net payable' : 'Total amount'}
          </div>
          <b className="cr-amount cr-amount--sm">
            {formatAmount(chargeAmount)}
          </b>
        </div>
        <AppButton
          btnText="Book & Pay"
          onClick={handlePay}
          disabled={loading}
          style={{ height: '44px', padding: '0 22px', fontWeight: 700 }}
        />
      </div>
    </CruiseLayout>
  );
}

export default CruiseGuestsPage;
